import { PermissionsEnum } from 'consts/permissions';
import Auth from 'modules/Auth';
import { getAsDate, MaybeDate } from './getAsDate';

export const SECONDS_IN_MINUTE = 60;
export const MINUTES_IN_HOUR = 60;
export const SECONDS_IN_HOUR = 3600;
export const ONE_DAY_IN_MS = 24 * MINUTES_IN_HOUR * SECONDS_IN_MINUTE * 1000;
export const ONE_DAY_IN_MINUTES = 24 * MINUTES_IN_HOUR;

export const formatISODateToDateObj = (isoDateString: string, isUTC = true): Date => {
  if (!isUTC) {
    return new Date(isoDateString);
  }

  const isoDate = new Date(isoDateString);
  const timezoneOffset = isoDate.getTimezoneOffset() * 60000;

  return new Date(isoDate.getTime() + timezoneOffset);
};

export const formatDateObjToIsoDateString = (dateObj: MaybeDate, isUTC = true): string => {
  const newDate = getAsDate(dateObj, null);
  if (!newDate) return '';

  const timezoneOffset = newDate.getTimezoneOffset() * 60000;
  const isoDateString = new Date(newDate.getTime() - timezoneOffset).toISOString();

  if (!isUTC) {
    return isoDateString.replace('Z', '');
  }

  return isoDateString;
};

export const getISOStringWithoutSecAndMillisec = (date: Date): string => {
  return formatDateObjToIsoDateString(date).replace(/:[^:]*$/, '');
};

export const getISOStringWithoutTime = (date: MaybeDate): string => {
  return formatDateObjToIsoDateString(date).replace(/[Tt]([^Zz]*)[Zz]$/, '');
};

export const getDaylightSavingOffset = (startDate: Date, endDate: Date): number =>
  (endDate.getTimezoneOffset() - startDate.getTimezoneOffset()) / ONE_DAY_IN_MINUTES;

export const getDaysDiff = (startDate: MaybeDate, endDate: MaybeDate): number => {
  const startDateCast = getAsDate(startDate, null);
  const endDateCast = getAsDate(endDate, null);
  if (!startDateCast || !endDateCast) return NaN;

  const daysDiff = (endDateCast.getTime() - startDateCast.getTime()) / ONE_DAY_IN_MS;
  const daylightSavingOffset = getDaylightSavingOffset(startDateCast, endDateCast);
  return daysDiff - daylightSavingOffset;
};

export const getCurrentDateWithTimezone = (timeZoneToUse = Intl.DateTimeFormat().resolvedOptions().timeZone): Date => {
  try {
    // eslint-disable-next-line no-new
    new Intl.DateTimeFormat('en-US', { timeZone: timeZoneToUse });
  } catch (error) {
    return new Date();
  }

  return new Date(new Date().toLocaleString('en-US', { timeZone: timeZoneToUse }));
};

export const getWeekdayNameFromDate = (date: Date, timezone: string, isShort = false): string => {
  return date.toLocaleDateString('en-US', { weekday: isShort ? 'short' : 'long', timeZone: timezone });
};

export const getMonth = (date: Date): string => date.toLocaleString('en-US', { month: 'long' });

export const getHoursAndMinutesFromDate = (date: Date, timezone: string): string => {
  return date.toLocaleTimeString([], { hourCycle: 'h23', hour: '2-digit', minute: '2-digit', timeZone: timezone });
};

export const getNoonTimeFromDate = (date: MaybeDate): string => {
  const newDate = getAsDate(date, null);
  if (!newDate) return '';

  const timezoneOffset = newDate.getTimezoneOffset() * 60000;
  const formattedDate = new Date(newDate.getTime() - timezoneOffset);

  return formattedDate.toLocaleTimeString('en-US', {
    hour12: true,
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

export const convertHoursAndMinutesToSeconds = (hours: number, minutes: number): number => hours * 3600 + minutes * 60;

export const parseDateMonthDayYear = (date: Date): string =>
  `${getMonth(date)} ${date.getDate()}, ${date.getFullYear()}`;

export const formatDateToMatchVCRequestPayload = (date: MaybeDate): string => {
  const [formattedDate] = formatDateObjToIsoDateString(
    date,
    Auth.hasPermission(PermissionsEnum.FEATURE_FLAG_TIMEZONE_UTC),
  ).split('.');

  return formattedDate;
};

export const secondsToHHmm = (seconds: number): string | false => {
  if (!Number.isInteger(seconds)) return false;

  return new Date((seconds % 86400) * 1000).toISOString().substring(11, 16);
};

export const formatDDMMYYYYDate = (date: MaybeDate, localeCode: string): string => {
  const dateObj = getAsDate(date, null);
  if (!dateObj || !localeCode) return '';

  return dateObj.toLocaleDateString(localeCode, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

export const formatDateToFnsDate = (fmt: string): string =>
  fmt
    .replace(/\bDD\b/g, 'dd')
    .replace(/\bYYYY\b/g, 'yyyy')
    .replace(/\bYY\b/g, 'yy')
    .replace(/\bD\b/g, 'd');
